import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { CountryReferenceValuesDto, Flag64Dto, MceApiService, ProjectDto, ProjectReferenceValuesDto } from '../../../core/mceapi.client.generated';
import {  appOpt } from '../../../core/mceapi.client.helpers';
import { isSome } from '../../../shared/functions/functional';
import { FormField, SchemaPathTree, min, max, required, FieldTree, disabled } from '@angular/forms/signals';

export interface ProjectDefaultValues {
  country: string;
  soilThermalResistivity: number;
  groundAmbientTemperature: number;
  airAmbientTemperature: number;
}
export function buildProjectDefaultValuesSchema(path: SchemaPathTree<ProjectDefaultValues>) {


  required(path.soilThermalResistivity, { message: `Soil Thermal Resistivity is required` });
  min(path.soilThermalResistivity, 0.5, { message: `Soil Thermal Resistivity must be at least 0.5` });
  max(path.soilThermalResistivity, 5.0, { message: `Soil Thermal Resistivity must be at most 5.0` });

  required(path.groundAmbientTemperature, { message: `Ground Ambient Temperature is required` });
  min(path.groundAmbientTemperature, -20, { message: `Ground Ambient Temperature must be at least -20` });
  max(path.groundAmbientTemperature, 80, { message: `Ground Ambient Temperature must be at most 80` });

  required(path.airAmbientTemperature, { message: `Air Ambient Temperature is required` });
  min(path.airAmbientTemperature, -20, { message: `Air Ambient Temperature must be at least -20` });
  max(path.airAmbientTemperature, 95, { message: `Air Ambient Temperature must be at most 95` });

  disabled(path.country, () => disableCountry());

}
const disableCountry = signal(true); // enables setting in code

export function createProjectDefaultValues(project: ProjectDto) {
  const referenceValues = project.referenceValues ?? {};

  return signal<ProjectDefaultValues> ({
    country: project.country ?? '',
    soilThermalResistivity: referenceValues.soilThermalResistivity ?? 1.2,
    groundAmbientTemperature: referenceValues.groundAmbientTemperature ?? 20,
    airAmbientTemperature: referenceValues.airAmbientTemperature ?? 25
  });
}

export function createProjectReferenceValuesDto(defaultValues: FieldTree<ProjectDefaultValues,string>) {
  return signal<ProjectReferenceValuesDto>({
    soilThermalResistivity: defaultValues.soilThermalResistivity().value(),
    groundAmbientTemperature: defaultValues.groundAmbientTemperature().value(),
    airAmbientTemperature: defaultValues.airAmbientTemperature().value()
  });
}

@Component({
  selector: 'project-edit-default-values',
  imports: [FormField, MatInputModule, MatSelectModule],
  templateUrl: `./project-edit-default-values.component.html`,
  styleUrl: `./project-edit-default-values.component.scss`
})
export class ProjectDefaultValuesComponent {

  readonly form = input.required<FieldTree<ProjectDefaultValues>>();

  private mceApiService = inject(MceApiService);

  countriesResource = rxResource({
    stream: () => this.mceApiService.v1Countries()
  });
  countries = computed(() => this.countriesResource.value() ?? []);

  flag64Resource = rxResource({
    params: () => ({ isoAlpha2: this.form().country().value() }),
    stream: ({ params }) => this.mceApiService.v1CountriesFlag64(params.isoAlpha2)
  });
  flag64 = computed<string>(() => {
    const flag64 = appOpt<Flag64Dto>(this.flag64Resource.value() ?? { isSome: false, isNone: true, value: [] });
    return isSome(flag64) ? flag64.value.flag64 ?? '' : '';
  });

  defaultValuesResource = rxResource({
    params: () => ({ countryCode: this.form().country().value() }),
    stream: ({ params }) => this.mceApiService.v1CountriesReferenceValues(params.countryCode)
  });
  defaultValues = computed<CountryReferenceValuesDto>(() => this.defaultValuesResource.value() ?? {});

  _disabled = effect(() => disableCountry.set(this.countriesResource.isLoading()));

  _resetDefaultValues = effect(() => {
    if (!this.form().country().dirty()) return;
      const dv = this.defaultValues();
      this.form().soilThermalResistivity().value.set(dv?.soilThermalResistivity ?? 1.2);
      this.form().groundAmbientTemperature().value.set(dv?.groundAmbientTemperature ?? 20);
      this.form().airAmbientTemperature().value.set(dv?.airAmbientTemperature ?? 25);
  });


}
